import {
  App as AntApp,
  Button,
  Card,
  Descriptions,
  Empty,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FileSearchOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  presignedDownload,
  useCustomer,
  useDeleteCustomer,
  useReactivateCustomer,
} from '../api/customers';
import { CustomerFormModal } from '../components/customer-form-modal';
import { useCustomerBalance, useCustomerTimeline } from '@/features/reports/api/reports';
import { formatUzs } from '@/shared/lib/format';
import { asApiError } from '@/shared/lib/api';
import { ADMIN_ROLES, RoleGate } from '@/shared/auth/role';

export function CustomerDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const { data: customer, isLoading } = useCustomer(id);
  const del = useDeleteCustomer();
  const reactivate = useReactivateCustomer();
  const [editOpen, setEditOpen] = useState(false);

  if (isLoading) return <Skeleton active />;
  if (!customer) return <Empty />;

  const fullName = [customer.lastName, customer.firstName, customer.middleName].filter(Boolean).join(' ');

  async function handleDelete() {
    if (!customer) return;
    try {
      await del.mutateAsync(customer.id);
      void message.success(t('customers.deleted'));
      navigate('/customers');
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  async function handleReactivate() {
    if (!customer) return;
    try {
      await reactivate.mutateAsync(customer.id);
      void message.success(t('customers.reactivated'));
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
          {t('customers.title')}
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
          <Space direction="vertical" size={2}>
            <Space size={10} align="center">
              <Typography.Title level={2} style={{ margin: 0 }}>{fullName}</Typography.Title>
              {customer.status === 'delinquent' && (
                <Tag color="red">{t('customers.status.delinquent')}</Tag>
              )}
            </Space>
            <Space size={12}>
              <Typography.Text type="secondary"><PhoneOutlined /> {customer.phone}</Typography.Text>
              {customer.email && <Typography.Text type="secondary"><MailOutlined /> {customer.email}</Typography.Text>}
            </Space>
          </Space>
          <Space>
            <Tag>{t(`customers.source.${customer.source}`)}</Tag>
            {customer.status === 'delinquent' && (
              <RoleGate roles={ADMIN_ROLES}>
                <Popconfirm
                  title={t('customers.reactivateConfirmTitle')}
                  description={t('customers.reactivateConfirmBody')}
                  okText={t('customers.reactivate')}
                  cancelText={t('common.cancel')}
                  okButtonProps={{ loading: reactivate.isPending }}
                  onConfirm={handleReactivate}
                >
                  <Button
                    icon={<CheckCircleOutlined />}
                    style={{ background: '#16a34a', color: '#fff', borderColor: '#16a34a' }}
                  >
                    {t('customers.reactivate')}
                  </Button>
                </Popconfirm>
              </RoleGate>
            )}
            <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
              {t('common.edit')}
            </Button>
            <Popconfirm
              title={t('customers.deleteConfirmTitle')}
              description={t('customers.deleteConfirmBody')}
              okText={t('common.delete')}
              cancelText={t('common.cancel')}
              okButtonProps={{ danger: true, loading: del.isPending }}
              onConfirm={handleDelete}
            >
              <Button danger icon={<DeleteOutlined />}>{t('common.delete')}</Button>
            </Popconfirm>
          </Space>
        </div>
      </div>

      <CustomerFormModal
        mode="edit"
        open={editOpen}
        customer={customer}
        onClose={() => setEditOpen(false)}
      />

      <Tabs
        items={[
          {
            key: 'overview',
            label: t('customers.tabs.overview'),
            children: <OverviewTab customer={customer} />,
          },
          {
            key: 'balance',
            label: t('customers.tabs.balance'),
            children: <BalanceTab customerId={customer.id} />,
          },
          {
            key: 'timeline',
            label: t('customers.tabs.timeline'),
            children: <TimelineTab customerId={customer.id} />,
          },
        ]}
      />
    </Space>
  );
}

function BalanceTab({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useCustomerBalance(customerId);
  if (isLoading) return <Skeleton active />;
  if (!data) return <Empty />;
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Card>
        <Descriptions column={{ xs: 1, sm: 2, md: 4 }} colon={false} labelStyle={{ color: '#64748b' }}>
          <Descriptions.Item label={t('customers.balance.contracted')}>
            <Typography.Text strong>{formatUzs(data.totals.contracted)}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.balance.paid')}>
            <Typography.Text strong style={{ color: '#16a34a' }}>
              {formatUzs(data.totals.paid)}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.balance.outstanding')}>
            <Typography.Text strong style={{ color: '#dc2626' }}>
              {formatUzs(data.totals.outstanding)}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.balance.lateFees')}>
            {formatUzs(data.totals.lateFees)}
          </Descriptions.Item>
          {data.totals.overdueRows > 0 && (
            <Descriptions.Item label={t('customers.balance.overdueRows')} span={4}>
              <Tag color="red">{data.totals.overdueRows}</Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
      <Card title={t('customers.tabs.contracts')} bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          dataSource={data.contracts}
          pagination={false}
          size="small"
          columns={[
            { title: t('contracts.cols.number'), dataIndex: 'contractNumber', width: 180, render: (v: string, r: { id: string }) => <Link to={`/contracts/${r.id}`}><Typography.Text code>{v}</Typography.Text></Link> },
            { title: t('contracts.cols.status'), dataIndex: 'status', width: 130, render: (s: string) => <Tag>{t(`contracts.status.${s}`)}</Tag> },
            { title: t('contracts.cols.totalAmount'), dataIndex: 'totalAmount', render: (v: string) => formatUzs(v) },
          ]}
        />
      </Card>
    </Space>
  );
}

function TimelineTab({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useCustomerTimeline(customerId, 100);
  if (isLoading) return <Skeleton active />;
  if (!data || data.length === 0) return <Empty description={t('customers.timeline.empty')} />;
  return (
    <Card bodyStyle={{ padding: 16 }}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {data.map((it, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 12,
              borderLeft: '2px solid #e2e8f0',
              paddingLeft: 12,
              paddingBottom: 6,
            }}
          >
            <Typography.Text type="secondary" style={{ width: 160, fontSize: 12 }}>
              {dayjs(it.ts).format('DD.MM.YYYY HH:mm')}
            </Typography.Text>
            <div style={{ flex: 1 }}>
              <Tag>{it.kind}</Tag>
              <Typography.Text style={{ fontSize: 13 }}>
                {JSON.stringify(it.payload)}
              </Typography.Text>
            </div>
          </div>
        ))}
      </Space>
    </Card>
  );
}

function OverviewTab({ customer }: { customer: NonNullable<ReturnType<typeof useCustomer>['data']> }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [opening, setOpening] = useState(false);

  async function openPassportFile() {
    if (!customer.passportFileUrl) return;
    setOpening(true);
    try {
      const url = await presignedDownload(customer.passportFileUrl);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      void message.error(asApiError(err).message);
    } finally {
      setOpening(false);
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title={t('customers.documents')}>
        <Descriptions column={{ xs: 1, sm: 2 }} colon={false} labelStyle={{ color: '#64748b' }}>
          <Descriptions.Item label={t('customers.passport_')}>
            {customer.passportSeries && customer.passportNumber
              ? `${customer.passportSeries} ${customer.passportNumber}`
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.passportIssueDate')}>
            {customer.passportIssueDate ? new Date(customer.passportIssueDate).toLocaleDateString('ru-RU') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.passportExpiryDate')}>
            {customer.passportExpiryDate ? new Date(customer.passportExpiryDate).toLocaleDateString('ru-RU') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.passportIssuedBy')}>
            {customer.passportIssuedBy ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.birthDate')}>
            {customer.dateOfBirth ? new Date(customer.dateOfBirth).toLocaleDateString('ru-RU') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.pinfl')}>{customer.pinfl ?? '—'}</Descriptions.Item>
        </Descriptions>
        {customer.passportFileUrl && (
          <div style={{ marginTop: 12 }}>
            <Button icon={<FileSearchOutlined />} loading={opening} onClick={openPassportFile}>
              {t('customers.viewPassport')}
            </Button>
          </div>
        )}
      </Card>

      <Card title={t('customers.contact')}>
        <Descriptions column={{ xs: 1, sm: 2 }} colon={false} labelStyle={{ color: '#64748b' }}>
          <Descriptions.Item label={t('customers.phone')}>{customer.phone}</Descriptions.Item>
          <Descriptions.Item label={t('customers.additionalPhones')}>
            {customer.additionalPhones?.length ? customer.additionalPhones.join(', ') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.email')}>{customer.email ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('customers.address')}>{customer.address ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('customers.registrationAddress')}>{customer.registrationAddress ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('customers.assignedAgent')}>{customer.assignedAgent?.fullName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('customers.preferredLanguage')}>{customer.preferredLanguage.toUpperCase()}</Descriptions.Item>
          {customer.notes && (
            <Descriptions.Item label={t('customers.notes')} span={2}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{customer.notes}</span>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title={t('customers.standing.title')}>
        <Descriptions column={{ xs: 1, sm: 2 }} colon={false} labelStyle={{ color: '#64748b' }}>
          <Descriptions.Item label={t('customers.standing.status')}>
            {customer.status === 'delinquent'
              ? <Tag color="red">{t('customers.status.delinquent')}</Tag>
              : <Tag color="green">{t('customers.status.active')}</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label={t('customers.standing.cumulativeLateDays')}>
            <Typography.Text strong style={{ color: customer.cumulativeLateDays > 0 ? '#dc2626' : undefined }}>
              {customer.cumulativeLateDays}
            </Typography.Text>
            {' '}{t('settings.days')}
          </Descriptions.Item>
          {customer.delinquentAt && (
            <Descriptions.Item label={t('customers.standing.delinquentAt')}>
              {new Date(customer.delinquentAt).toLocaleDateString('ru-RU')}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
    </Space>
  );
}
